import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { axios } from "@via/api/axios.tsx";
import { RUN_ENVIRONMENT_BASE } from "@via/pages/account/profile/run-environments/data-roots.api.tsx";
import type {
  RunEnvironmentContainerRuntimeResponseBody,
  SaveRunEnvironmentContainerRuntimeRequestBody,
  SaveRunEnvironmentContainerRuntimeResponseBody,
} from "common";

/**
 * The container runtime routes of a run environment (backend/src/routes/mountAllowlist.ts). One GET
 * and one PUT carry the runtime, the HPC app blocks (agent-9-1) and the workspace caps (agent-7-4),
 * so the Apps card and the Container execution card read and write through the same hooks and the
 * same cache entry. Owner, organization admin or platform admin only: anyone else gets a 403.
 */
const LIVE_POLL_MS = 5000;

export const containerRuntimeQueryKey = (
  environmentId?: number
): (string | number | undefined)[] => [
  "run-environment-container-runtime",
  environmentId,
];

export const fetchContainerRuntime = async (
  environmentId: number
): Promise<RunEnvironmentContainerRuntimeResponseBody> => {
  const res = await axios.get(
    `${RUN_ENVIRONMENT_BASE}/${environmentId}/container-runtime`
  );
  return res.data as RunEnvironmentContainerRuntimeResponseBody;
};

/** Polls every 5 seconds while an apps check is pending, so its result appears on its own. */
export const useContainerRuntime = (
  environmentId?: number
): UseQueryResult<RunEnvironmentContainerRuntimeResponseBody, Error> =>
  useQuery<RunEnvironmentContainerRuntimeResponseBody, Error>({
    queryKey: containerRuntimeQueryKey(environmentId),
    queryFn: () => fetchContainerRuntime(environmentId!),
    enabled: Boolean(environmentId),
    retry: false,
    refetchInterval: query =>
      query.state.data?.apps?.probe.state === "pending" ? LIVE_POLL_MS : false,
  });

export const useSaveContainerRuntime = (
  environmentId?: number
): UseMutationResult<
  SaveRunEnvironmentContainerRuntimeResponseBody,
  unknown,
  SaveRunEnvironmentContainerRuntimeRequestBody
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveRunEnvironmentContainerRuntimeRequestBody) => {
      const res = await axios.put(
        `${RUN_ENVIRONMENT_BASE}/${environmentId}/container-runtime`,
        body
      );
      return res.data as SaveRunEnvironmentContainerRuntimeResponseBody;
    },
    onSuccess: (): void => {
      queryClient.invalidateQueries({
        queryKey: containerRuntimeQueryKey(environmentId),
      });
    },
  });
};
